import {
  useDatePickerNavigationContext,
  useDatePickerSelectionContext,
  useDatePickerSettingsContext,
} from '@app/components/various/DatePicker/context';
import { cn } from '@app/utils/cn';
import { getIsDateBetween } from '@app/utils/dates';
import { isNull } from '@app/utils/is';
import { DatePickerNs } from '@typings';
import { Dayjs } from 'dayjs';
import React from 'react';
import { mergeRefs } from 'react-laag';

interface Props {
  date: Dayjs;
  shouldFocus?: boolean;
}

const CELL_FORMAT_MAP: Record<DatePickerNs.View, string> = {
  date: 'D',
  month: 'MMM',
  year: 'YYYY',
};

export const DatePickerCalendarCell = ({ date, shouldFocus }: Props) => {
  const cellRef = React.useRef<HTMLButtonElement>(null);

  const { view, isDateView, isYearView, setView, shouldDisable, getShouldShowDot } = useDatePickerSettingsContext();
  const { visibleDate, setVisibleDate, visibleDateCellRef } = useDatePickerNavigationContext();
  const { endDatePreview, startDate, endDate, isRangeSelection, handleSelect, setEndDatePreview } = useDatePickerSelectionContext();

  const isVisibleDate = date.isSame(visibleDate, view);

  React.useEffect(() => {
    if (isVisibleDate && shouldFocus) {
      cellRef.current?.focus();
    }
  }, [isVisibleDate]);

  const isDisabled = shouldDisable(date);

  const isSelected = React.useMemo(() => {
    return date.isSame(startDate, view) || date.isSame(endDate, view);
  }, [date, endDate, startDate, view]);

  const isOutOfView = React.useMemo(() => {
    return isDateView && !date.isSame(visibleDate, 'month');
  }, [date, isDateView, visibleDate]);

  const handleClick = React.useCallback(() => {
    if (isDisabled) {
      return;
    }

    if (isDateView) {
      handleSelect(date);
    }

    setVisibleDate(date);
    setView(isYearView ? 'month' : 'date');
  }, [isDisabled, isDateView, setVisibleDate, date, setView, isYearView, handleSelect]);

  const shouldAddListeners = isRangeSelection && !isNull(startDate) && isNull(endDate);
  const rangeListeners = {
    onFocus: () => setEndDatePreview(date),
    onMouseEnter: () => setEndDatePreview(date),
    onMouseLeave: () => setEndDatePreview(null),
  };

  const isStartDate = date.isSame(startDate, view);
  const isEndDate = date.isSame(endDate, view);
  const isEndDatePreview = date.isSame(endDatePreview, view);
  const isInRange = getIsDateBetween(date, [startDate, endDate], view);
  const isInPreviewRange = getIsDateBetween(date, [startDate, endDatePreview], view);
  const isStartEndSame = startDate?.isSame(endDate, view);
  const isDateAfterDate = startDate?.diff(endDatePreview, 'day') === -1 || startDate?.diff(endDate, 'day') === -1;

  const isStartEndPreviewSame = startDate?.isSame(endDatePreview, view);

  const cellLabel = date.format(CELL_FORMAT_MAP[view]);

  const shouldGrayOut = isOutOfView || isDisabled;

  const commonBeforeStyles = 'before:absolute before:h-full before:w-1/2 before:bg-neutral-96';

  return (
    <button
      ref={isVisibleDate ? mergeRefs(cellRef, visibleDateCellRef) : cellRef}
      type="button"
      role="gridcell"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      aria-label={date.format('YYYY MM DD')}
      tabIndex={isVisibleDate ? 0 : -1}
      className={cn(
        'group/cell flex w-full flex-1 justify-center rounded-none body-1 before:pointer-events-none focus-visible:outline-none',
        {
          'bg-neutral-96': ((isInRange && !isEndDate) || (isInPreviewRange && !isEndDatePreview)) && !isStartDate,
          [`${commonBeforeStyles} before:-left-2`]:
            ((isInRange && isEndDate) || (isInPreviewRange && isEndDatePreview)) && !isDateAfterDate && !isStartDate,
          [`${commonBeforeStyles} before:-right-3.5`]:
            (isInRange || isInPreviewRange) && isStartDate && !isStartEndPreviewSame && !isStartEndSame,
          'cursor-not-allowed': isDisabled,
          'text-neutral-80': shouldGrayOut,
        },
        'relative h-8 w-8',
      )}
      onClick={handleClick}
      {...(shouldAddListeners && rangeListeners)}
    >
      <span
        className={cn(
          'relative flex h-8 w-3/4 min-w-8 items-center justify-center rounded-small hover:bg-neutral-96 group-focus-within/cell:focus-outline',
          {
            'after:absolute after:left-0.5 after:top-0.5 after:h-1.5 after:w-1.5 after:rounded-full after:bg-info-62': getShouldShowDot?.(
              date,
              view,
            ),
            'after:bg-current': isSelected,
            'bg-neutral-20 text-white hover:bg-neutral-40 group-hover/cell:after:bg-current':
              !isDisabled && (isSelected || isStartDate || isEndDate || isEndDatePreview),
          },
        )}
      >
        {cellLabel}
      </span>
    </button>
  );
};
